/**
 * @file Provides Microsoft OneDrive uploading capability in the frontend.
 *
 * Directly tries Beethoven Uploads for OneDrive files
 *
 */

import { trackEvent } from '@@/bits/analytics'
import { customAjax } from '@@/bits/custom_ajax'
import { captureMessage } from '@@/bits/error_tracker'
import { cleanupFilename } from '@@/bits/file_handler'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { fetchJson } from '@@/surface/api_fetch'
import type { MicrosoftOneDriveUploadResponse } from '@@/types'
import { HTTPMethod } from '@padlet/fetch'
import EventEmitter from 'events'

const BEETHOVEN_API_VERSION = 2

const trackError = function (label: string, context: any = {}): void {
  trackEvent('Microsoft OneDrive Integration Uploads', 'Error', label)
  context.fileType = label
  captureMessage('UploadError', { context })
}

const trackSuccess = function (label: string): void {
  trackEvent('Microsoft OneDrive Integration Uploads', 'Upload succeeded', label)
}

export class UploadMicrosoftOneDriveFileJob extends EventEmitter {
  private fileId: string | null = null
  private driveId: string | null = null
  private endpoint: string | null = null
  private accessToken: string | null = null
  private readonly fileName: string | null = null
  private readonly fileType: string | null = null
  private readonly fileSize: number | null = null

  constructor({
    fileName,
    fileType,
    fileSize,
  }: {
    fileName: string | null
    fileType: string | null
    fileSize: number | null
  }) {
    super()
    this.fileName = fileName
    this.fileType = fileType
    this.fileSize = fileSize
  }

  async uploadMicrosoftOneDriveFile({
    fileId,
    driveId,
    endpoint,
    accessToken,
  }: {
    fileId: string | null
    driveId: string | null
    endpoint: string | null
    accessToken: string | null
  }): Promise<void> {
    this.fileId = fileId
    this.driveId = driveId
    this.endpoint = endpoint
    this.accessToken = accessToken

    if (this.fileId == null) {
      throw new Error('Valid fileId must be provided')
    }

    if (this.driveId == null) {
      throw new Error('Valid driveId must be provided')
    }

    if (this.endpoint == null) {
      throw new Error('Valid endpoint must be provided')
    }

    if (this.accessToken == null) {
      throw new Error('Valid accessToken must be provided')
    }

    await this.performMicrosoftOneDriveFileUpload()
  }

  async uploadMicrosoftOneDriveUrl({
    fileId,
    driveId,
    endpoint,
    accessToken,
  }: {
    fileId: string | null
    driveId: string | null
    endpoint: string | null
    accessToken: string | null
  }): Promise<void> {
    this.fileId = fileId
    this.driveId = driveId
    this.endpoint = endpoint
    this.accessToken = accessToken

    if (this.fileId == null) {
      throw new Error('Valid fileId must be provided')
    }

    if (this.driveId == null) {
      throw new Error('Valid driveId must be provided')
    }

    if (this.endpoint == null) {
      throw new Error('Valid endpoint must be provided')
    }

    if (this.accessToken == null) {
      throw new Error('Valid accessToken must be provided')
    }

    await this.performMicrosoftOneDriveUrlUpload()
  }

  async performMicrosoftOneDriveUrlUpload(): Promise<void> {
    // Guard clause to return error if file is too large
    try {
      const response: MicrosoftOneDriveUploadResponse = await fetchJson(
        `/beethoven-v${BEETHOVEN_API_VERSION}/microsoft-onedrive-file`,
        {
          method: HTTPMethod.post,
          body: asciiSafeStringify({
            microsoft_onedrive_url: true,
            file_id: this.fileId,
            drive_id: this.driveId,
            endpoint: this.endpoint,
            access_token: this.accessToken,
          }),
        },
      )

      trackSuccess(this.fileType ?? 'unknown')
      this.emitDone(response)
    } catch (error) {
      if (error.status === 'abort') return
      trackError(this.fileType ?? 'unknown', {
        fileId: this.fileId,
        driveId: this.driveId,
        endpoint: this.endpoint,
        error,
      })
      this.emitError(error)
    }
  }

  async performMicrosoftOneDriveFileUpload(): Promise<void> {
    // Makes a call to `uploads_controller`, to get a BeethovenUploader
    // instance specific to Microsoft OneDrive that will provide a url and postback url to hit.
    customAjax({
      url: '/media/upload_attributes.json',
      dataType: 'json',
      type: 'GET',
      cache: false,
      sendOauthToken: true,
      data: {
        unique: new Date(),
        // this is ultimately what tells the upload controller to use the Microsoft OneDrive Beethoven endpoint
        isMicrosoftOneDriveFile: true,
      },
      error: (responseData, textStatus) => {
        if (textStatus === 'abort') return
        trackError('startUploadAjax', {
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        this.emitError(responseData as unknown as MicrosoftOneDriveUploadResponse)
      },
      success: (beethovenUploaderAttributes) => {
        void this.hitBeethovenCompletionUrl(beethovenUploaderAttributes)
      },
    })
  }

  private async hitBeethovenCompletionUrl(beethovenUploaderAttributes: any): Promise<void> {
    // Disable as wel need this alias to call the emit functions
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this
    const fileUrl = beethovenUploaderAttributes.url
    const requestData = {}
    const fieldsOverrides = {
      transcode: true,
    }
    const beethovenCompletionAttributes = Object.assign({}, beethovenUploaderAttributes.fields, fieldsOverrides)

    Object.entries(beethovenCompletionAttributes).forEach((attribute) => {
      if (attribute[0] !== 'key') {
        requestData[attribute[0]] = attribute[1]
      }
    })

    try {
      const response: MicrosoftOneDriveUploadResponse = await fetchJson(fileUrl, {
        method: HTTPMethod.post,
        body: asciiSafeStringify({
          key: [beethovenUploaderAttributes.fields.key, cleanupFilename(this.fileName ?? 'untitled')].join('/'),
          file_size: this.fileSize?.toString(),
          file_id: this.fileId,
          drive_id: this.driveId,
          endpoint: this.endpoint,
          access_token: this.accessToken,
          title: this.fileName,
          url: fileUrl,
          ...requestData,
        }),
      })

      trackSuccess(this.fileType ?? 'unknown')
      self.emitDone(response)
    } catch (error) {
      if (error.status === 'abort') return
      trackError(this.fileType ?? 'unknown', {
        userId: beethovenCompletionAttributes.uploader_id,
        filename: this.fileName,
        fileSize: this.fileSize,
        maxFileSize: beethovenCompletionAttributes.max_file_size,
        error,
      })
      self.emitError(error)
    }
  }

  /*
   * Emits
   */
  private emitDone(doneObject: MicrosoftOneDriveUploadResponse): void {
    this.emit('done', doneObject)
  }

  private emitError(errorObject: MicrosoftOneDriveUploadResponse): void {
    this.emit('error', errorObject)
  }
}
